/**
 * Supplier Group Detail Page
 *
 * Shows group-level information for a CompanyGroup, including
 * aggregate stats and member companies with links to their detail pages.
 * CR-4579
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authAPI, coreAPI, CompanyGroup, CompanyGroupMembersResponse, CompanyListItem, UserProfile } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { usePortalMode } from '../hooks/usePortalMode';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Loader2,
  ArrowLeft,
  Building2,
  Package,
  Truck,
  ChevronRight,
  Network,
  Users,
  Globe,
  ExternalLink,
  UserPlus,
  Mail,
  MoreVertical,
  UserCog,
} from 'lucide-react';
// CR-5311: reuse the User Management modal + action wiring so clicking
// a user opens the same modal in-place, instead of navigating away.
import { UserDetailsModal } from './admin/UserDetailsModal';
import { useUserActions } from './admin/useUserActions';

export default function SupplierGroupDetail() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { formatCurrency } = useFormatters();
  const { user, startImpersonation } = useAuth();
  const { isCustomer, isHelix } = usePortalMode();
  const userActions = useUserActions();
  const isSelf = (userProfile: { user: { id: number } }) => userProfile.user.id === user?.id;
  const isSuperAdmin = Boolean(user?.is_superuser);
  // CR-5316: gate the per-row "Login As" action the same way Supplier
  // Detail's Users tab does. Backend re-enforces this in
  // ImpersonateView, so the frontend gate is just to hide the option
  // for users who can't use it.
  const canImpersonate = isHelix || isCustomer;

  const [group, setGroup] = useState<CompanyGroup | null>(null);
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [stats, setStats] = useState<CompanyGroupMembersResponse['stats'] | null>(null);
  // CR-5311: group-level user list — sourced from /company-groups/{id}/users/
  // so admins can see and add users at the group level instead of drilling
  // into each member company.
  const [groupUsers, setGroupUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState<number | null>(null);

  const fetchGroupData = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError(null);
    try {
      const id = parseInt(groupId, 10);
      const [members, users] = await Promise.all([
        coreAPI.getCompanyGroupMembers(id),
        coreAPI.getCompanyGroupUsers(id).catch(() => ({ data: { count: 0, results: [] as UserProfile[] } })),
      ]);
      setGroup(members.data.group);
      setCompanies(members.data.companies);
      setStats(members.data.stats);
      setGroupUsers(users.data.results);
    } catch (err: unknown) {
      console.error('Failed to fetch group data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load group details');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    fetchGroupData();
  }, [fetchGroupData]);

  // CR-5316: "Login As" handler for users in the group-level Users
  // card. The group spans multiple companies, so pin the post-
  // impersonation landing scope to the *clicked user's* own company —
  // matches Supplier Detail's Users tab semantics, where the admin
  // lands on the company they were inspecting.
  const handleImpersonate = async (u: UserProfile) => {
    if (!canImpersonate) return;
    try {
      setImpersonating(u.user.id);
      const response = await authAPI.impersonate(u.user.id, undefined, u.company);
      const impersonationData = response.data.data;
      const contextCompany = u.company
        ? { id: u.company, name: u.company_name }
        : undefined;
      startImpersonation(
        impersonationData.target_user,
        impersonationData.target_token,
        impersonationData.original_token,
        contextCompany,
      );
    } catch (err: unknown) {
      console.error('Error impersonating user:', err);
    } finally {
      setImpersonating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error || 'Group not found'}</p>
          <Button onClick={() => navigate('/suppliers')} variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Suppliers
          </Button>
        </div>
      </div>
    );
  }

  const orderValue = parseFloat(stats?.total_order_value || '0');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/suppliers')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Network className="h-6 w-6 text-primary" />
              <h1 className="text-3xl font-bold tracking-tight">{group.name}</h1>
              <Badge className={group.is_active
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }>
                {group.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <p className="text-muted-foreground">Supplier Group</p>
          </div>
        </div>
      </div>

      {/* Group Info */}
      {(group.description || group.headquarters_country || group.website) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Group Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-3">
              {group.description && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Description</p>
                  <p className="text-sm">{group.description}</p>
                </div>
              )}
              {group.headquarters_country && (
                <div className="flex items-start gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Headquarters</p>
                    <p className="text-sm">{group.headquarters_country}</p>
                  </div>
                </div>
              )}
              {group.website && (
                <div className="flex items-start gap-2">
                  <ExternalLink className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Website</p>
                    <a
                      href={group.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      {group.website}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Aggregate Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Companies
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total_companies}</div>
              <p className="text-xs text-muted-foreground mt-1">member companies</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Package className="h-4 w-4" />
                Total Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total_orders}</div>
              <p className="text-xs text-muted-foreground mt-1">purchase orders</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Package className="h-4 w-4" />
                Order Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{formatCurrency(orderValue)}</div>
              <p className="text-xs text-muted-foreground mt-1">total value</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Truck className="h-4 w-4" />
                Total GRNs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total_grns}</div>
              <p className="text-xs text-muted-foreground mt-1">goods receipts</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Member Companies List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Member Companies ({companies.length})
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {companies.length > 0 ? (
            <div className="space-y-2">
              {companies.map((company) => (
                <div
                  key={company.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/suppliers/${company.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{company.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {company.city ? `${company.city}, ` : ''}{company.country || 'No location'}
                        {company.primary_email ? ` - ${company.primary_email}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {company.user_count} {company.user_count === 1 ? 'user' : 'users'}
                    </Badge>
                    <Badge className={company.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                    }>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No companies in this group.
            </p>
          )}
        </CardContent>
      </Card>

      {/* CR-5311: Group-level Users card. Lists every UserProfile attached
          to any company in the group; "Add User" reuses the existing
          /users/invite flow. Selecting a user opens the standard User
          Details modal (Suppliers > Member > Users path) which now shows
          all sibling sites in the Related Companies tab. */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Users ({groupUsers.length})
            </CardTitle>
            <Button
              size="sm"
              onClick={() => navigate(`/users/invite?type=supplier&group=${group.id}`)}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {groupUsers.length > 0 ? (
            <div className="space-y-2">
              {groupUsers.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => userActions.openUserDetails(u)}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600 font-semibold text-sm">
                      {((u.user.first_name?.[0] ?? '') + (u.user.last_name?.[0] ?? '')).toUpperCase() || '??'}
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {u.user.first_name || u.user.last_name
                          ? `${u.user.first_name ?? ''} ${u.user.last_name ?? ''}`.trim()
                          : u.user.email}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-2">
                        <Mail className="h-3 w-3" />
                        {u.user.email}
                        <span className="opacity-50">·</span>
                        {u.company_name ?? '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.role_name && (
                      <Badge variant="outline" className="text-xs">{u.role_name}</Badge>
                    )}
                    <Badge className={u.is_approved
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                    }>
                      {u.is_approved ? 'Active' : 'Pending'}
                    </Badge>
                    {canImpersonate && (
                      <>
                        {/* CR-5316 round 2: surface "Login As" as an
                            explicit labelled button on the row instead of
                            tucked inside a kebab. Round 1 hid it inside
                            MoreVertical and the customer reported
                            "no option" because the icon was too easy to
                            miss. Keep the kebab for "View Details" so the
                            row stays compact. */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          disabled={
                            isSelf(u) ||
                            !u.user.is_active ||
                            impersonating === u.user.id
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImpersonate(u);
                          }}
                        >
                          {impersonating === u.user.id ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          ) : (
                            <UserCog className="h-3.5 w-3.5 mr-1" />
                          )}
                          Login As
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            asChild
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                userActions.openUserDetails(u);
                              }}
                            >
                              <Users className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleImpersonate(u);
                              }}
                              disabled={
                                isSelf(u) ||
                                !u.user.is_active ||
                                impersonating === u.user.id
                              }
                            >
                              {impersonating === u.user.id ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <UserCog className="h-4 w-4 mr-2" />
                              )}
                              Login As
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No users yet. Click <strong>Add User</strong> to invite the first one.
            </p>
          )}
        </CardContent>
      </Card>

      {/* CR-5311: User Details modal opened in-place when a user row is
          clicked. Closing the modal returns the admin to the group page —
          no navigation away. Refetch the group's user list after a save
          so role / status changes from the modal are reflected
          immediately. */}
      <UserDetailsModal
        open={userActions.showUserDetails}
        onOpenChange={(openVal) => {
          userActions.setShowUserDetails(openVal);
          if (!openVal) fetchGroupData();
        }}
        selectedUser={userActions.selectedUser}
        userDetailTab={userActions.userDetailTab}
        setUserDetailTab={userActions.setUserDetailTab}
        userSitePermissions={userActions.userSitePermissions}
        savingPermission={userActions.savingPermission}
        isSuperAdmin={isSuperAdmin}
        actionLoading={userActions.actionLoading}
        isSelf={isSelf}
        handleToggleActive={userActions.handleToggleActive}
        openChangeRoleDialog={userActions.openChangeRoleDialog}
        handleToggleAdmin={userActions.handleToggleAdmin}
        handleToggleSiteLink={userActions.handleToggleSiteLink}
        handleToggleSitePermission={userActions.handleToggleSitePermission}
        handleApproveUser={userActions.handleApproveUser}
        handleRejectUser={userActions.handleRejectUser}
        handleSendInvitation={userActions.handleSendInvitation}
      />
    </div>
  );
}
